<script lang="ts" setup>
import { computed, type PropType } from 'vue';
import { useI18n } from 'vue-i18n';

import BigNumber from '@exchange/helpers/bignumber';
import { MarketState } from '@exchange/libs/market/service/src';
import type { MarketModel } from '@exchange/libs/market/service/src';
import type { OrderWithTradesModel } from '@exchange/libs/order/shared-model/src/lib/order-model';
import { CONSTANTS } from '@exchange/libs/utils/constants/src';

const props = defineProps({
  order: { type: Object as PropType<OrderWithTradesModel>, required: true },
  market: { type: Object as PropType<MarketModel> },
});

const getTweezingFilledPercentShow = (n: number) => {
  const dfPrecision = CONSTANTS.PRECISION.DEFAULT_PERCENTAGE;
  const pow = new BigNumber(10).pow(dfPrecision);

  return new BigNumber(n).div(pow).toFixed(dfPrecision);
};

const { t } = useI18n({ useScope: 'global' });

const marketIsSuspended = computed(() => !!props.market && props.market.stateIs(MarketState.SUSPENDED));
const marketIsInMaintenance = computed(() => !!props.market && props.market.stateIs(MarketState.IN_MAINTENANCE));
const marketIsSuspendedOrMaintained = computed(() => marketIsSuspended.value || marketIsInMaintenance.value);

const getOrderStatusTranslation = (percent) => t(`fundamentals.orders.status.${props.order.status}`, { percent });
const orderStatus = computed(() => getOrderStatusTranslation(props.order.filledPercent));
const status = computed(() => {
  if (marketIsSuspended.value) {
    return t('fundamentals.orders.status.SUSPENDED');
  }

  if (marketIsSuspendedOrMaintained.value) {
    return t('fundamentals.orders.status.IN_MAINTENANCE');
  }

  return orderStatus.value;
});

const tweezingFilledPercentNumber = computed(() => {
  // tweezing can only deal with non decimal numbers -> need to multiply
  const pow = new BigNumber(10).pow(CONSTANTS.PRECISION.DEFAULT_PERCENTAGE);

  return new BigNumber(props.order.filledPercent).times(pow).toNumber();
});
const getTweezingFilledPercentText = (percent) => {
  const percentToShow = getTweezingFilledPercentShow(percent);

  return getOrderStatusTranslation(percentToShow);
};
</script>

<template>
  <div
    class="order-status"
    testid="order-status"
  >
    <template v-if="marketIsSuspendedOrMaintained">{{ status }}</template>
    <template v-else>
      <ot-tweezer
        :to="tweezingFilledPercentNumber"
        :get-shown-value="getTweezingFilledPercentText"
      >
        <template #default="{ tweezedValue }">{{ tweezedValue }}</template>
      </ot-tweezer>
    </template>
  </div>
</template>

<style lang="scss">
.order-status {
  color: var(--status-color);
}
</style>
